import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  isSupported,
  getToken,
  onMessage,
  type Messaging,
} from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

function configValid() {
  return Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);
}

export function initFirebase(): FirebaseApp | null {
  if (!configValid()) {
    if (import.meta.env.DEV) {
      console.warn('[firebase] Skipping init — env not configured. Fill .env to enable.');
    }
    return null;
  }
  if (getApps().length) return getApps()[0]!;
  app = initializeApp(firebaseConfig);
  return app;
}

export async function getMessagingClient(): Promise<Messaging | null> {
  if (messaging) return messaging;
  if (!(await isSupported())) return null;
  const instance = initFirebase();
  if (!instance) return null;
  messaging = getMessaging(instance);
  return messaging;
}

export async function getWebPushToken(): Promise<string | null> {
  const m = await getMessagingClient();
  if (!m) return null;
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (!vapidKey) return null;
  try {
    return await getToken(m, { vapidKey });
  } catch (e) {
    console.error('[firebase] getToken failed', e);
    return null;
  }
}

export async function onWebPushMessage(handler: (payload: unknown) => void) {
  const m = await getMessagingClient();
  if (!m) return () => {};
  return onMessage(m, handler);
}
